import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Upload, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

const ColorRow = ({ modelId, color, rec, reload }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const s = slug(color.name);
  const pending = rec?.status === "pending";
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await reload(); } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const generate = () => run(() => api.post(`/models/${modelId}/color-image`, { color: color.name, force: true }), "Génération IA lancée (environ 30 s)");
  const upload = (file) => run(async () => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await api.put(`/admin/models/${modelId}/color-image`, { color: color.name, url: data.url });
  }, "Photo enregistrée");
  const remove = () => { if (window.confirm(`Retirer la photo « ${color.name} » ? Une nouvelle image IA sera créée au prochain devis dans cette couleur.`)) run(() => api.put(`/admin/models/${modelId}/color-image`, { color: color.name, url: "" }), "Photo retirée"); };
  return (
    <div className="flex items-center gap-3 border-b border-border py-2" data-testid={`color-photo-${s}`}>
      <span className="w-5 h-5 rounded border border-border shrink-0" style={{ background: color.hex }} />
      <span className="text-sm w-36 shrink-0">{color.name}</span>
      <div className="w-24 h-16 rounded border border-border bg-muted/30 flex items-center justify-center overflow-hidden shrink-0">
        {pending ? <Loader2 className="w-4 h-4 animate-spin text-primary" />
          : rec?.status === "ready" ? <img src={`${BACKEND}${rec.url}`} alt={color.name} className="w-full h-full object-cover" data-testid={`color-photo-img-${s}`} />
          : <span className="text-[10px] text-muted-foreground text-center px-1">{rec?.status === "error" ? "Échec IA" : "Photo du modèle"}</span>}
      </div>
      <span className="text-[11px] text-muted-foreground w-20 shrink-0" data-testid={`color-photo-source-${s}`}>{rec?.status === "ready" ? (rec.source === "manual" ? "Vraie photo" : "Image IA") : ""}</span>
      <div className="flex flex-wrap gap-1.5">
        <Button type="button" size="sm" variant="outline" disabled={busy || pending} onClick={generate} className="gap-1 h-8" data-testid={`color-photo-ai-${s}`}><Sparkles className="w-3.5 h-3.5" /> IA</Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="gap-1 h-8" data-testid={`color-photo-upload-${s}`}><Upload className="w-3.5 h-3.5" /> Vraie photo</Button>
        {rec && <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove} className="h-8 text-destructive" data-testid={`color-photo-remove-${s}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
      </div>
    </div>
  );
};

export const ColorPhotos = ({ modelId, colors }) => {
  const [recs, setRecs] = useState({});
  const reload = useCallback(async () => {
    const { data } = await api.get(`/admin/models/${modelId}/color-images`);
    setRecs(Object.fromEntries(data.map((d) => [d.color, d])));
  }, [modelId]);
  useEffect(() => { reload().catch(() => {}); }, [reload]);
  const anyPending = Object.values(recs).some((r) => r.status === "pending");
  useEffect(() => {
    if (!anyPending) return undefined;
    const t = setInterval(() => { reload().catch(() => {}); }, 5000);
    return () => { clearInterval(t); };
  }, [anyPending, reload]);
  const list = (colors || []).filter((c) => !/ral/i.test(c.name));
  if (!list.length) return null;
  return (
    <div className="rounded-lg border border-border p-3" data-testid="color-photos">
      <div className="text-sm font-semibold">Photos par couleur (devis PDF)</div>
      <p className="text-[11px] text-muted-foreground mb-1">Une image IA est créée automatiquement la première fois qu'un vendeur choisit la couleur. Remplacez-la par une vraie photo quand vous en avez une. Sans photo, le PDF montre la photo du modèle.</p>
      {list.map((c) => <ColorRow key={c.name} modelId={modelId} color={c} rec={recs[c.name]} reload={reload} />)}
    </div>
  );
};
